import type { BufferGeometry } from 'three';

/**
 * Some supplied CAD exports contain collapsed triangles. Recomputed normals are
 * zero on those faces; invalid lighting values can then spread through the
 * half-float transmission mipmaps as black rectangles. Exclude the faces before
 * computing normals, retaining all vertex attributes and material/draw ranges.
 */
export function removeProformDegenerateTriangles(geometry: BufferGeometry): number {
  const position = geometry.getAttribute('position');
  if (!position || position.itemSize < 3) return 0;
  const sourceIndex = geometry.getIndex();
  const count = Math.floor((sourceIndex?.count ?? position.count) / 3) * 3;
  const indices: number[] = [];
  const offsets = new Uint32Array(count + 1);
  let removed = 0;

  for (let offset = 0; offset + 2 < count; offset += 3) {
    const a = sourceIndex ? sourceIndex.getX(offset) : offset;
    const b = sourceIndex ? sourceIndex.getX(offset + 1) : offset + 1;
    const c = sourceIndex ? sourceIndex.getX(offset + 2) : offset + 2;
    const abX = position.getX(b) - position.getX(a);
    const abY = position.getY(b) - position.getY(a);
    const abZ = position.getZ(b) - position.getZ(a);
    const acX = position.getX(c) - position.getX(a);
    const acY = position.getY(c) - position.getY(a);
    const acZ = position.getZ(c) - position.getZ(a);
    const crossX = abY * acZ - abZ * acY;
    const crossY = abZ * acX - abX * acZ;
    const crossZ = abX * acY - abY * acX;
    const areaSquared = crossX * crossX + crossY * crossY + crossZ * crossZ;
    if (Number.isFinite(areaSquared) && areaSquared > 0) indices.push(a, b, c);
    else removed += 1;
    offsets[offset + 1] = offsets[offset + 2] = offsets[offset + 3] = indices.length;
  }

  if (!removed) return 0;
  const remap = (offset: number) => offsets[Math.min(Math.max(offset, 0), count)];
  for (const group of geometry.groups) {
    const start = remap(group.start);
    group.count = remap(group.start + group.count) - start;
    group.start = start;
  }
  const { start, count: drawCount } = geometry.drawRange;
  geometry.setDrawRange(remap(start), remap(start + drawCount) - remap(start));
  geometry.setIndex(indices);
  return removed;
}
